import { useCallback, useMemo, useEffect, useState } from 'react'
import { arrayToObject } from 'lib/array/arrayUtils'
import { AITripPreferenceStep, Locations } from '../GenerateTripPage'
import { MustDoActivityItem } from 'tripPlanner/hooks/api/mustDoActivities'
import { ThemeItem } from 'tripPlanner/hooks/api/tripGenerationData'

const SURPRISE_ME_WITH_LOCATION_STEPS: Array<AITripPreferenceStep> = ['start', 'occupancy', 'datePicker']
const SURPRISE_ME_WITHOUT_LOCATION_STEPS: Array<AITripPreferenceStep> = ['start', 'location', 'occupancy', 'datePicker']
// For orderId/cartId: Skip location, occupancy, datePicker (use order/cart data)
const ORDER_CART_STEPS: Array<AITripPreferenceStep> = ['start', 'pace', 'proximity', 'budget']
// For offerId: Skip location (use offer location), but include datePicker & occupancy
const OFFER_ID_STEPS: Array<AITripPreferenceStep> = ['start', 'occupancy', 'datePicker', 'pace', 'proximity', 'budget']
// For URL location only: Include datePicker & occupancy
const WITH_LOCATION_STEPS: Array<AITripPreferenceStep> = ['start', 'occupancy', 'datePicker', 'pace', 'proximity', 'budget']
// For no location: Include all steps
const DEFAULT_STEPS: Array<AITripPreferenceStep> = ['start', 'location', 'occupancy', 'datePicker', 'pace', 'proximity', 'budget']

interface Props {
  step: AITripPreferenceStep
  setStep: (step: AITripPreferenceStep) => void
  mustDoActivities?: Array<MustDoActivityItem>
  theme?: Array<ThemeItem>
  hasOrderId?: boolean
  hasCartId?: boolean
  hasOfferId?: boolean
  hasOrderCartOrOfferLocation?: boolean
  urlLocation?: string
  isSurpriseMe?: boolean
  setIsSurpriseMe?: (value: boolean) => void
  locations?: Array<Locations>
  mustDoActivitiesByLocation?: Map<string, Array<MustDoActivityItem>>
}

export function useStepNavigation({ step, setStep, mustDoActivities, theme, hasOrderId, hasCartId, hasOfferId, hasOrderCartOrOfferLocation, urlLocation, isSurpriseMe, setIsSurpriseMe, locations, mustDoActivitiesByLocation }: Props) {
  const [currentMustDoLocationIndex, setCurrentMustDoLocationIndex] = useState(0)

  const { totalSteps, stepNumbers, stepSequence } = useMemo(() => {
    let baseSteps: Array<AITripPreferenceStep>

    if (isSurpriseMe) {
      if (hasOrderCartOrOfferLocation || urlLocation) {
        baseSteps = SURPRISE_ME_WITH_LOCATION_STEPS
      } else {
        baseSteps = SURPRISE_ME_WITHOUT_LOCATION_STEPS
      }
      return {
        totalSteps: baseSteps.length - 1,
        stepNumbers: arrayToObject(baseSteps, (step: AITripPreferenceStep) => step, (step: AITripPreferenceStep, i: number) => i),
        stepSequence: baseSteps,
      }
    } else if (hasOrderId || hasCartId) {
      baseSteps = ORDER_CART_STEPS
    } else if (hasOfferId) {
      baseSteps = OFFER_ID_STEPS
    } else if (hasOrderCartOrOfferLocation || urlLocation) {
      baseSteps = WITH_LOCATION_STEPS
    } else {
      baseSteps = DEFAULT_STEPS
    }
    const stepSequences: Array<AITripPreferenceStep> = theme ?
        [...baseSteps, 'theme'] :
        [...baseSteps]

    let stepSequence: Array<AITripPreferenceStep>

    if (locations && locations.length > 0 && mustDoActivitiesByLocation) {
      const mustDoSteps: Array<AITripPreferenceStep> = locations
        .map((location, index): { step: AITripPreferenceStep; index: number } => ({ step: `mustDo${index}`, index }))
        .filter(({ index }) => {
          const location = locations[index]
          const activities = location?.locationName ? mustDoActivitiesByLocation.get(location.locationName) : undefined
          return activities && activities.length > 0
        })
        .map(({ step }) => step)
      stepSequence = [...stepSequences, ...mustDoSteps, 'foodStyle', 'freeText']
    } else if (mustDoActivities && mustDoActivities.length > 0) {
      stepSequence = [...stepSequences, 'mustDo', 'foodStyle', 'freeText']
    } else {
      stepSequence = [...stepSequences, 'foodStyle', 'freeText']
    }

    const stepNumbers = arrayToObject(
      stepSequence,
      (step: AITripPreferenceStep) => step,
      (_step: AITripPreferenceStep, i: number) => i,
    )

    return {
      totalSteps: stepSequence.length - 1,
      stepNumbers,
      stepSequence,
    }
  }, [mustDoActivities, theme, hasOrderId, hasCartId, hasOfferId, hasOrderCartOrOfferLocation, urlLocation, isSurpriseMe, locations, mustDoActivitiesByLocation])

  useEffect(() => {
    if (step === 'start' && isSurpriseMe && setIsSurpriseMe) {
      setIsSurpriseMe(false)
    }
  }, [step, isSurpriseMe, setIsSurpriseMe])

  useEffect(() => {
    if (step.startsWith('mustDo')) {
      const match = step.match(/^mustDo(\d+)$/)
      if (match) {
        setCurrentMustDoLocationIndex(parseInt(match[1], 10))
      }
    }
  }, [step])

  const getPreviousStep = useCallback((currentStep: AITripPreferenceStep): AITripPreferenceStep => {
    if (currentStep === 'start') return 'start'
    const currentIndex = stepSequence.indexOf(currentStep)
    return currentIndex > 0 ? stepSequence[currentIndex - 1] : 'start'
  }, [stepSequence])

  const getNextStep = useCallback((currentStep: AITripPreferenceStep): AITripPreferenceStep => {
    const currentIndex = stepSequence.indexOf(currentStep)
    return currentIndex < stepSequence.length - 1 ? stepSequence[currentIndex + 1] : stepSequence.at(-1)!
  }, [stepSequence])

  const handleBack = useCallback(() => {
    setStep(getPreviousStep(step))
  }, [getPreviousStep, step, setStep])

  const handleNext = useCallback(() => {
    setStep(getNextStep(step))
  }, [getNextStep, step, setStep])

  return {
    totalSteps,
    stepNumbers,
    stepSequence,
    getPreviousStep,
    getNextStep,
    handleBack,
    handleNext,
    currentMustDoLocationIndex,
  }
}
